import { useContext, useEffect, useState, useCallback } from 'react';
import { WebSocketClientContext } from '.';
import { WebSocketClientMessageTypes } from './messageTypes';
import { useTimeOut } from 'hooks/useTimeOut';

export function useWebSocketClientSubscription(
  messageType: WebSocketClientMessageTypes,
  memoizedCallback: (message: any) => void
) {
  const webSocketClientContext = useContext(WebSocketClientContext);

  useEffect(() => {
    const id = webSocketClientContext.subscribe(messageType, memoizedCallback);
    return () => {
      if (id) webSocketClientContext.unsubscribe(id);
    };
  }, [messageType, memoizedCallback]);
}

export function useWebSocketClientMessageSubscription(
  messageType: WebSocketClientMessageTypes,
  timeOut?: number
) {
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>();
  const webSocketClientContext = useContext(WebSocketClientContext);
  const timer = useTimeOut();

  const unsubscribe = useCallback(() => {
    if (!subscriptionId) return;
    webSocketClientContext.unsubscribe(subscriptionId);
    setSubscriptionId(undefined);
    timer.clearTimeOut();
  }, [subscriptionId]);

  const subscribe = useCallback(
    (onMessageReceived: (message: any) => void, onTimeout?: () => void) => {
      const id = webSocketClientContext.subscribe(
        messageType,
        (wbMessage: any) => {
          timer.clearTimeOut();
          unsubscribe();
          onMessageReceived(wbMessage);
        }
      );
      setSubscriptionId(id);

      if (timeOut && onTimeout) {
        timer.setTimeOut(onTimeout, timeOut);
      }
    },
    [messageType, unsubscribe, timer]
  );

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  return {
    subscribe,
    unsubscribe,
    isListening: !!subscriptionId,
  };
}

export function useWebSocketClientMessageSubscriptionV2(
  messageType: WebSocketClientMessageTypes,
  timeOut?: number
) {
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>();
  const webSocketClientContext = useContext(WebSocketClientContext);
  const timer = useTimeOut();

  const unsubscribe = useCallback(() => {
    if (!subscriptionId) return;
    webSocketClientContext.unsubscribe(subscriptionId);
    setSubscriptionId(undefined);
    timer.clearTimeOut();
  }, [subscriptionId]);

  const subscribe = useCallback(
    (onMessageReceived: (message: any) => void, onTimeout?: () => void) => {
      const id = webSocketClientContext.subscribe(
        messageType,
        (wbMessage: any) => {
          timer.clearTimeOut();
          onMessageReceived(wbMessage);
        }
      );
      setSubscriptionId(id);

      if (timeOut && onTimeout) {
        timer.setTimeOut(onTimeout, timeOut);
      }
    },
    [messageType, unsubscribe, timer]
  );

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    subscribe,
    unsubscribe,
    isListening: !!subscriptionId,
  };
}
